import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  Certification,
  CoverageDay,
  RosterShift,
  ShiftType,
  Staff,
  StaffRequest,
} from '@/lib/types';
import {
  Banner,
  Card,
  PageHeader,
  Pill,
  cellStyle,
  fieldStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Select } from '@/components/Select';
import { useSort, SortTh } from '@/components/sortable';

const SHIFTS: ShiftType[] = [
  'MORNING',
  'FULL',
  'EVENING',
  'OFF',
  'LEAVE',
  'TRAINING',
  'ONCALL',
];
const SHIFT_LABEL: Record<ShiftType, string> = {
  MORNING: '早班',
  FULL: '全天',
  EVENING: '晚班',
  OFF: '休假',
  LEAVE: '請假',
  TRAINING: '訓練',
  ONCALL: '待命',
};
const REQ_LABEL: Record<string, string> = { LEAVE: '請假', SWAP: '換班', OT: '加班' };
const STATUS_LABEL: Record<string, string> = {
  PENDING: '待審',
  APPROVED: '已核准',
  REJECTED: '已駁回',
};

export function SchedulingPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [form, setForm] = useState({
    staffId: '',
    date: '',
    shiftType: 'FULL' as ShiftType,
  });
  const [reqForm, setReqForm] = useState({
    staffId: '',
    type: 'LEAVE' as 'LEAVE' | 'SWAP' | 'OT',
    startDate: '',
    endDate: '',
    reason: '',
  });

  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.get<Staff[]>('/catalog/staff') });
  const roster = useQuery({ queryKey: ['roster'], queryFn: () => api.get<RosterShift[]>('/scheduling/roster') });
  const coverage = useQuery({ queryKey: ['coverage'], queryFn: () => api.get<CoverageDay[]>('/scheduling/coverage') });
  const requests = useQuery({ queryKey: ['requests'], queryFn: () => api.get<StaffRequest[]>('/scheduling/requests') });
  const certs = useQuery({ queryKey: ['certs'], queryFn: () => api.get<Certification[]>('/scheduling/certs') });
  const expired = useQuery({ queryKey: ['certs-expired'], queryFn: () => api.get<Certification[]>('/scheduling/certs/expired') });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['roster'] });
    void qc.invalidateQueries({ queryKey: ['coverage'] });
  };
  const nameOf = (id: string) => staff.data?.find((s) => s.id === id)?.name ?? id;

  const upsert = useMutation({
    mutationFn: () =>
      api.post('/scheduling/shifts', {
        staffId: form.staffId,
        date: new Date(form.date).toISOString(),
        shiftType: form.shiftType,
      }),
    onSuccess: () => {
      ok('班別已更新');
      refresh();
    },
    onError: fail,
  });

  const decide = useMutation({
    mutationFn: (v: { id: string; verb: 'approve' | 'reject' }) =>
      api.post(`/scheduling/requests/${v.id}/${v.verb}`),
    onSuccess: () => {
      ok('已處理請求');
      void qc.invalidateQueries({ queryKey: ['requests'] });
    },
    onError: fail,
  });

  const createReq = useMutation({
    mutationFn: () =>
      api.post('/scheduling/requests', {
        staffId: reqForm.staffId,
        type: reqForm.type,
        startDate: new Date(reqForm.startDate).toISOString(),
        endDate: new Date(reqForm.endDate).toISOString(),
        reason: reqForm.reason || undefined,
      }),
    onSuccess: () => {
      ok('申請已送出');
      setReqForm({ staffId: '', type: 'LEAVE', startDate: '', endDate: '', reason: '' });
      void qc.invalidateQueries({ queryKey: ['requests'] });
    },
    onError: fail,
  });

  const expiredIds = new Set((expired.data ?? []).map((c) => c.id));

  const sortRoster = useSort(roster.data ?? [], {
    date: (r) => r.date,
    staff: (r) => nameOf(r.staffId),
    shift: (r) => SHIFT_LABEL[r.shiftType],
  });
  const sortCoverage = useSort(coverage.data ?? [], {
    date: (c) => c.date,
    doctors: (c) => c.doctors,
    consultants: (c) => c.consultants,
    status: (c) => (c.understaffed ? 0 : 1),
  });
  const sortRequests = useSort(requests.data ?? [], {
    staff: (r) => nameOf(r.staffId),
    type: (r) => REQ_LABEL[r.type],
    period: (r) => r.startDate,
    status: (r) => STATUS_LABEL[r.status],
  });
  const sortCerts = useSort(certs.data ?? [], {
    staff: (c) => nameOf(c.staffId),
    skill: (c) => c.skill,
    expire: (c) => c.expireAt,
    status: (c) => (expiredIds.has(c.id) ? 0 : 1),
  });

  return (
    <div>
      <PageHeader title="排班輪值" subtitle="人力覆蓋稽核（醫師<2 或 顧問<1 即告警）；請假/換班/加班審批；證照到期停用排班" />
      <Banner msg={msg} />

      <Card title="排定班別">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select
            value={form.staffId}
            onChange={(val) => setForm((f) => ({ ...f, staffId: val }))}
            placeholder="— 選擇員工 —"
            options={(staff.data ?? []).map((s) => ({ value: s.id, label: `${s.name}（${s.role}）` }))}
          />
          <input type="date" style={fieldStyle} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
          <Select
            value={form.shiftType}
            onChange={(val) => setForm((f) => ({ ...f, shiftType: val as ShiftType }))}
            options={SHIFTS.map((s) => ({ value: s, label: SHIFT_LABEL[s] }))}
          />
          <button className="btn btn-primary" disabled={!form.staffId || !form.date || upsert.isPending} onClick={() => upsert.mutate()}>
            排定
          </button>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Card title="本期班表">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><SortTh label="日期" sortKey="date" sort={sortRoster} /><SortTh label="員工" sortKey="staff" sort={sortRoster} /><SortTh label="班別" sortKey="shift" sort={sortRoster} /></tr></thead>
            <tbody>
              {sortRoster.sorted.map((r) => (
                <tr key={r.id}>
                  <td style={cellStyle} className="num">{r.date.slice(0, 10)}</td>
                  <td style={cellStyle}>{nameOf(r.staffId)}</td>
                  <td style={cellStyle}>{SHIFT_LABEL[r.shiftType]}</td>
                </tr>
              ))}
              {roster.data?.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={3}>尚無班表</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card title="人力覆蓋稽核">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><SortTh label="日期" sortKey="date" sort={sortCoverage} /><SortTh label="醫師" sortKey="doctors" sort={sortCoverage} /><SortTh label="顧問" sortKey="consultants" sort={sortCoverage} /><SortTh label="狀態" sortKey="status" sort={sortCoverage} /></tr></thead>
            <tbody>
              {sortCoverage.sorted.map((c) => (
                <tr key={c.date}>
                  <td style={cellStyle} className="num">{c.date}</td>
                  <td style={cellStyle} className="num">{c.doctors}</td>
                  <td style={cellStyle} className="num">{c.consultants}</td>
                  <td style={cellStyle}>
                    {c.understaffed
                      ? <Pill bg="var(--danger-bg)" color="var(--danger)">{c.reasons.join('；')}</Pill>
                      : <Pill bg="var(--success-bg)" color="var(--success)">充足</Pill>}
                  </td>
                </tr>
              ))}
              {coverage.data?.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>尚無資料</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>

      <Card title="假勤 / 換班 / 加班審批">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <Select
            value={reqForm.staffId}
            onChange={(val) => setReqForm((f) => ({ ...f, staffId: val }))}
            placeholder="— 申請員工 —"
            options={(staff.data ?? []).map((s) => ({ value: s.id, label: `${s.name}` }))}
          />
          <Select
            value={reqForm.type}
            onChange={(val) => setReqForm((f) => ({ ...f, type: val as 'LEAVE' | 'SWAP' | 'OT' }))}
            options={[
              { value: 'LEAVE', label: '請假' },
              { value: 'SWAP', label: '換班' },
              { value: 'OT', label: '加班' },
            ]}
          />
          <input type="date" style={fieldStyle} value={reqForm.startDate} onChange={(e) => setReqForm((f) => ({ ...f, startDate: e.target.value }))} />
          <input type="date" style={fieldStyle} value={reqForm.endDate} onChange={(e) => setReqForm((f) => ({ ...f, endDate: e.target.value }))} />
          <input style={{ ...fieldStyle, flex: 1, minWidth: 120 }} placeholder="事由（選填）" value={reqForm.reason} onChange={(e) => setReqForm((f) => ({ ...f, reason: e.target.value }))} />
          <button className="btn btn-primary" disabled={!reqForm.staffId || !reqForm.startDate || !reqForm.endDate || createReq.isPending} onClick={() => createReq.mutate()}>送出申請</button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><SortTh label="員工" sortKey="staff" sort={sortRequests} /><SortTh label="類型" sortKey="type" sort={sortRequests} /><SortTh label="期間" sortKey="period" sort={sortRequests} /><SortTh label="狀態" sortKey="status" sort={sortRequests} /><th style={thStyle}>操作</th></tr></thead>
          <tbody>
            {sortRequests.sorted.map((r) => (
              <tr key={r.id}>
                <td style={cellStyle}>{nameOf(r.staffId)}</td>
                <td style={cellStyle}>{REQ_LABEL[r.type]}</td>
                <td style={cellStyle} className="num">{r.startDate.slice(0, 10)} ~ {r.endDate.slice(0, 10)}</td>
                <td style={cellStyle}>{STATUS_LABEL[r.status]}</td>
                <td style={{ ...cellStyle, display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost" disabled={r.status !== 'PENDING'} onClick={() => decide.mutate({ id: r.id, verb: 'approve' })}>核准</button>
                  <button className="btn btn-ghost" disabled={r.status !== 'PENDING'} onClick={() => decide.mutate({ id: r.id, verb: 'reject' })}>駁回</button>
                </td>
              </tr>
            ))}
            {requests.data?.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={5}>尚無申請</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title="證照效期">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><SortTh label="員工" sortKey="staff" sort={sortCerts} /><SortTh label="技能" sortKey="skill" sort={sortCerts} /><SortTh label="到期日" sortKey="expire" sort={sortCerts} /><SortTh label="狀態" sortKey="status" sort={sortCerts} /></tr></thead>
          <tbody>
            {sortCerts.sorted.map((c) => (
              <tr key={c.id}>
                <td style={cellStyle}>{nameOf(c.staffId)}</td>
                <td style={cellStyle}>{c.skill}</td>
                <td style={cellStyle} className="num">{c.expireAt.slice(0, 10)}</td>
                <td style={cellStyle}>
                  {expiredIds.has(c.id)
                    ? <Pill bg="var(--danger-bg)" color="var(--danger)">已過期·停用排班</Pill>
                    : <Pill bg="var(--success-bg)" color="var(--success)">有效</Pill>}
                </td>
              </tr>
            ))}
            {certs.data?.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>尚無證照</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
